import { useState, useEffect, useCallback } from 'react'
import {
  X,
  Bluetooth,
  BluetoothConnected,
  BluetoothSearching,
  Printer,
  AlertCircle,
  CheckCircle,
  Loader2,
  RefreshCw,
  Usb,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { printerService } from '@/lib/printer'
import type { PrinterDevice, PrinterState } from '@/lib/printer'
import { getPrinterSettings, setPrinterSettings } from '@/lib/printer/settings'
import type { PrinterConnectionType } from '@/lib/printer'
import { buildTestReceiptHtml, openReceiptPreview, printHtmlReceipt } from '@/lib/printer/system-print'

interface PrinterSelectionModalProps {
  isOpen: boolean
  onClose: () => void
  onPrinterConnected?: (device: PrinterDevice) => void
}

const getAdapterLabel = (adapterType: string) => {
  if (adapterType === 'system') return 'System Print'
  if (adapterType === 'webBluetooth') return 'Web Bluetooth'
  if (adapterType === 'native') return 'Native Bluetooth'
  if (adapterType === 'webUsb') return 'WebUSB'
  return 'Not available'
}

export function PrinterSelectionModal({
  isOpen,
  onClose,
  onPrinterConnected,
}: PrinterSelectionModalProps) {
  const [state, setState] = useState<PrinterState>(printerService.getState())
  const [isPrinting, setIsPrinting] = useState(false)
  const [printerSettings, setPrinterSettingsState] = useState(getPrinterSettings())

  useEffect(() => {
    const unsubscribe = printerService.subscribe(setState)
    return unsubscribe
  }, [])

  useEffect(() => {
    if (isOpen) {
      const settings = getPrinterSettings()
      setPrinterSettingsState(settings)
      printerService.setPreferredType(settings.preferredConnection)
      printerService.checkConnection()
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen) {
      setPrinterSettingsState(getPrinterSettings())
    }
  }, [isOpen])

  const handleScan = useCallback(async () => {
    const devices = await printerService.scan()
    if (devices.length > 0) {
      const success = await printerService.connect(devices[0].id)
      if (success && onPrinterConnected) {
        onPrinterConnected(devices[0])
      }
      if (success) {
        setPrinterSettingsState(setPrinterSettings({ printer: devices[0] }))
      }
    }
  }, [onPrinterConnected])

  const handleDisconnect = useCallback(async () => {
    await printerService.disconnect()
  }, [])

  const handleTestPrint = useCallback(async () => {
    setIsPrinting(true)
    try {
      if (printerSettings.preferredConnection === 'system') {
        const html = buildTestReceiptHtml()
        if (printerSettings.showPreviewBeforePrinting) {
          openReceiptPreview(html)
        } else {
          printHtmlReceipt(html)
        }
      } else {
        await printerService.printTestReceipt()
      }
    } finally {
      setIsPrinting(false)
    }
  }, [printerSettings.preferredConnection, printerSettings.showPreviewBeforePrinting])

  const handleConnectionTypeChange = (value: PrinterConnectionType) => {
    const next = setPrinterSettings({ preferredConnection: value })
    setPrinterSettingsState(next)
    printerService.setPreferredType(value)
  }

  const handlePreviewToggle = (checked: boolean) => {
    const next = setPrinterSettings({ showPreviewBeforePrinting: checked })
    setPrinterSettingsState(next)
  }

  const platformInfo = printerService.getPlatformInfo()
  const isSystemPrint = printerSettings.preferredConnection === 'system'

  if (!isOpen) return null

  const adapterIcon = platformInfo.adapterType === 'webUsb' ? Usb : Bluetooth

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative w-full max-w-md bg-white dark:bg-neutral-900 rounded-lg shadow-xl border border-neutral-200 dark:border-neutral-800 overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-neutral-200 dark:border-neutral-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center">
              <Printer className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Connect Printer</h2>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                {getAdapterLabel(platformInfo.adapterType)}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-md text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4">
          {!printerService.isSupported() && !isSystemPrint && (
            <div className="text-center py-8">
              <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-neutral-900 dark:text-neutral-100 mb-2">
                Printing Not Available
              </h3>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-4">
                {platformInfo.platform === 'web'
                  ? 'Printing requires Web Bluetooth or WebUSB (Chrome/Edge).'
                  : 'No supported printer adapter was detected.'}
              </p>
            </div>
          )}

          <div className="mb-4">
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Print Method
            </label>
            <select
              value={printerSettings.preferredConnection}
              onChange={(e) => handleConnectionTypeChange(e.target.value as PrinterConnectionType)}
              className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-700 rounded-md bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="system">System Print (macOS/Windows)</option>
              <option value="bluetooth">Bluetooth (BLE)</option>
              <option value="usb">USB (WebUSB)</option>
            </select>
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
              System Print uses your OS print dialog. WebUSB requires Chrome or Edge on desktop.
            </p>
          </div>

          {isSystemPrint && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Print Preview
              </label>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={printerSettings.showPreviewBeforePrinting}
                  onChange={(e) => handlePreviewToggle(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-neutral-300 dark:border-neutral-600 rounded"
                />
                <span className="ml-2 text-sm text-neutral-700 dark:text-neutral-300">
                  Show preview before printing
                </span>
              </div>
            </div>
          )}

          {printerService.isSupported() && !isSystemPrint && state.status === 'connected' && state.device && (
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                <div className="flex items-center gap-3">
                  <BluetoothConnected className="w-6 h-6 text-green-600 dark:text-green-400" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-neutral-900 dark:text-neutral-100 truncate">
                      {state.device.name}
                    </p>
                    <p className="text-sm text-green-600 dark:text-green-400">Connected</p>
                  </div>
                  <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
              </div>

              <div className="flex gap-3">
                <Button onClick={handleTestPrint} disabled={isPrinting} className="flex-1">
                  {isPrinting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Printing...
                    </>
                  ) : (
                    <>
                      <Printer className="w-4 h-4 mr-2" />
                      Test Print
                    </>
                  )}
                </Button>
                <Button variant="outline" onClick={handleDisconnect}>
                  Disconnect
                </Button>
              </div>
            </div>
          )}

          {printerService.isSupported() && !isSystemPrint && state.status === 'connecting' && (
            <div className="text-center py-8">
              <Loader2 className="w-12 h-12 text-blue-500 mx-auto mb-4 animate-spin" />
              <p className="text-neutral-600 dark:text-neutral-400">Connecting to printer...</p>
            </div>
          )}

          {printerService.isSupported() && !isSystemPrint &&
            (state.status === 'disconnected' || state.status === 'error') && (
              <div className="space-y-4">
                {state.error && (
                  <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700 dark:text-red-300">{state.error}</p>
                  </div>
                )}

                <div className="text-center py-6">
                  {state.isScanning ? (
                    <>
                      <BluetoothSearching className="w-16 h-16 text-blue-500 mx-auto mb-4 animate-pulse" />
                      <p className="text-neutral-600 dark:text-neutral-400 mb-2">Searching for printers...</p>
                      <p className="text-xs text-neutral-500 dark:text-neutral-500">
                        Select your printer from the browser dialog
                      </p>
                    </>
                  ) : (
                    <>
                      {(() => {
                        const Icon = adapterIcon
                        return <Icon className="w-16 h-16 text-neutral-400 mx-auto mb-4" />
                      })()}
                      <p className="text-neutral-600 dark:text-neutral-400 mb-4">
                        {platformInfo.adapterType === 'webUsb'
                          ? 'Connect to a USB thermal printer'
                          : 'Connect to a Bluetooth thermal printer'}
                      </p>
                      <Button onClick={handleScan} className="inline-flex items-center gap-2">
                        <RefreshCw className="w-4 h-4" />
                        Scan for Printers
                      </Button>
                    </>
                  )}
                </div>

                <div className="p-4 rounded-lg bg-neutral-50 dark:bg-neutral-800/50 space-y-2">
                  <p className="text-sm font-medium text-neutral-700 dark:text-neutral-200">Instructions:</p>
                  <ol className={cn(
                    'text-sm text-neutral-600 dark:text-neutral-400 space-y-1 list-decimal list-inside',
                    platformInfo.adapterType === 'webUsb' && 'list-decimal'
                  )}>
                    {platformInfo.adapterType === 'webUsb' ? (
                      <>
                        <li>Plug in your USB thermal printer</li>
                        <li>Click “Scan for Printers” above</li>
                        <li>Select your printer from the list</li>
                      </>
                    ) : (
                      <>
                        <li>Turn on your thermal printer</li>
                        <li>Enable Bluetooth on the printer</li>
                        <li>Click “Scan for Printers” above</li>
                        <li>Select your printer from the list</li>
                      </>
                    )}
                  </ol>
                </div>
              </div>
            )}

          {printerService.isSupported() && !isSystemPrint && state.status === 'printing' && (
            <div className="text-center py-8">
              <Loader2 className="w-12 h-12 text-blue-500 mx-auto mb-4 animate-spin" />
              <p className="text-neutral-600 dark:text-neutral-400">Printing...</p>
            </div>
          )}

          {isSystemPrint && (
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  System Print uses your computer’s print dialog and printers configured in macOS or Windows.
                </p>
              </div>
              <div className="flex gap-3">
                <Button onClick={handleTestPrint} disabled={isPrinting} className="flex-1">
                  {isPrinting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Printing...
                    </>
                  ) : (
                    <>
                      <Printer className="w-4 h-4 mr-2" />
                      Test Print
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="px-4 py-3 bg-neutral-50 dark:bg-neutral-900/50 border-t border-neutral-200 dark:border-neutral-800">
          <p className="text-xs text-neutral-500 dark:text-neutral-500 text-center">
            {isSystemPrint
              ? 'System Print uses your OS printer list'
              : 'Supported: ESC/POS compatible thermal printers'}
          </p>
        </div>
      </div>
    </div>
  )
}
